import React, { useState, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import SidebarItem from "./SidebarItem";
import LoadingScreen from "../LoadingScreen/LoadingScreen";
import "./Sidebar.scss";

const Sidebar = () => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const location = useLocation();
  const { user, hasRole, isAuthenticated, isLoading } = useAuth();

  const menuItems = useMemo(
    () => [
      {
        name: "Dashboard",
        icon: "material-symbols-light:dashboard-outline",
        to: "/dashboard",
        description: "Overview and analytics",
        roles: [
          "ROLE_CRM-ADMIN",
          "ROLE_INVENTORY-MANAGER",
          "ROLE_SALES",
          "ROLE_EMPLOYEE",
        ],
      },
      {
        name: "Leads",
        icon: "mdi:briefcase-outline",
        to: "/leads",
        description: "Manage sales leads",
        roles: ["ROLE_SALES"],
      },
      {
        name: "Enquiries",
        icon: "fluent:people-search-20-filled",
        to: "/enquiries",
        description: "Customer enquiries",
        roles: ["ROLE_SALES"],
      },
      {
        name: "Clients",
        icon: "tabler:users-group",
        to: "/clients",
        description: "Client management",
        roles: ["ROLE_SALES"],
      },
    ],
    [],
  );

  const erpItems = useMemo(
    () => [
      {
        name: "Teams",
        icon: "mdi:account-group-outline",
        to: "/team",
        description: "Manage employee teams and groups",
        roles: ["ROLE_PROD-MANAGER", "ROLE_SALES-MANAGER"],
      },
      {
        name: "Operations",
        icon: "mdi:cogs",
        to: "/operations",
        description: "Track production stages",
        roles: ["ROLE_CRM-ADMIN", "ROLE_OPERATIONS"],
      },
      {
        name: "Reports",
        icon: "mdi:chart-box-outline",
        to: "/reports/client",
        description: "Role-based operational reports",
        activePrefix: "/reports", // stays highlighted on every /reports/... page
        roles: [
          "ROLE_SALES-MANAGER",
          "ROLE_PROD-MANAGER",
          "ROLE_INV-MANAGER",
          "ROLE_INVENTORY-MANAGER",
        ],
      },
    ],
    [],
  );

  const filteredMenuItems = useMemo(
    () => menuItems.filter((item) => item.roles.some((role) => hasRole(role))),
    [menuItems, hasRole],
  );

  const filteredErpItems = useMemo(
    () =>
      erpItems
        .map((item) =>
          item.name === "Reports"
            ? {
                ...item,
                // Each manager lands on the first report they can open.
                to: hasRole("ROLE_SALES-MANAGER")
                  ? "/reports/overview"
                  : hasRole("ROLE_PROD-MANAGER")
                    ? "/reports/operation"
                    : "/reports/warehouse",
              }
            : item,
        )
        .filter((item) => item.roles.some((role) => hasRole(role))),
    [erpItems, hasRole],
  );

  const isActive = useCallback(
    (item) => {
      const base = item.activePrefix || item.to;
      return (
        location.pathname === base ||
        (base !== "/" && location.pathname.startsWith(base))
      );
    },
    [location.pathname],
  );

  const handleHover = useCallback((itemName) => setHoveredItem(itemName), []);
  const handleLeave = useCallback(() => setHoveredItem(null), []);
  const handleClick = useCallback((itemName) => {
    setHoveredItem(itemName);
    if (navigator.vibrate) navigator.vibrate(50);
  }, []);
  const handleKeyDown = useCallback(
    (event, item) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleClick(item.name);
      }
    },
    [handleClick],
  );

  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated || !user) return null;

  return (
    <nav className="sidebar" role="navigation" aria-label="Main navigation">
      <div className="sidebar-section">
        {filteredMenuItems.map((item, index) => (
          <SidebarItem
            key={item.name}
            item={item}
            isHovered={hoveredItem === item.name}
            isActive={isActive(item)}
            onHover={handleHover}
            onLeave={handleLeave}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="menuitem"
            aria-label={`${item.name} - ${item.description || ""}`}
            style={{ animationDelay: `${index * 50}ms` }}
          />
        ))}
      </div>

      <div className="sidebar-section">
        {filteredErpItems.map((item, index) => (
          <SidebarItem
            key={item.name}
            item={item}
            isHovered={hoveredItem === item.name}
            isActive={isActive(item)}
            isExpanded={
              item.name === "Reports" &&
              (location.pathname.startsWith("/reports") ||
                hoveredItem === item.name)
            }
            onHover={handleHover}
            onLeave={handleLeave}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="menuitem"
            aria-label={`${item.name} - ${item.description || ""}`}
            style={{ animationDelay: `${index * 50}ms` }}
          />
        ))}
      </div>
    </nav>
  );
};

export default Sidebar;
